import { trpc } from "@mnk/frontend/lib/trpc";
import type { Drawing } from "@mnk/shared/models/drawing";
import { useEffect, useMemo, useRef, useState } from "react";
import { Downloader } from "./Downloader";
import { A4_HEIGHT, A4_WIDTH } from "./const";
import { DrawingArea } from "./drawingArea";
import { Toolbar } from "./toolbar";
interface Prop {
  projectId: string;
  drawing: Drawing;
  quantityId: string;
  officeName: string;
}

export const Drawer = ({ projectId, drawing, quantityId, officeName }: Prop) => {
  const [zoomInitialized, setZoomInitialized] = useState(false);
  const [showDownloader, setShowDownloader] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showFlag, setShowFlag] = useState(true);

  const updateZoom = (zoom: number) => {
    const roundedZoom = Number(zoom.toFixed(2));
    setZoom(roundedZoom);
  };

  const { data: drawingImages, isLoading } = trpc.getDrawingImagesByDrawingId.useQuery(
    { drawingId: drawing.id },
    { enabled: !!drawing },
  );

  const { data: project } = trpc.getProjectById.useQuery({ projectId });

  const currentDrawingImage = useMemo(() => {
    return drawingImages?.[currentIndex];
  }, [drawingImages, currentIndex]);

  // 初期ズームを計算し、キャンバスを表示エリアにフィットさせる
  useEffect(() => {
    const initializeZoom = () => {
      if (!containerRef.current) {
        setTimeout(initializeZoom, 100);
        return;
      }

      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const horizontalZoom = (containerWidth - 40) / A4_WIDTH;
      const verticalZoom = (containerHeight - 40) / A4_HEIGHT;

      const fitZoom = Math.min(horizontalZoom, verticalZoom);
      const safeZoom = Math.max(fitZoom, 0.1);

      setZoomInitialized(true);
      setZoom(safeZoom);
    };

    initializeZoom();
  }, []); // マウント時に一度だけ実行

  // コンテナのリサイズを監視
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!zoomInitialized) return;

      const container = containerRef.current;
      if (!container) return;

      // 現在のズーム値が最小ズーム値より小さい場合のみ、コンテナサイズに合わせて調整
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const horizontalZoom = (containerWidth - 40) / A4_WIDTH;
      const verticalZoom = (containerHeight - 40) / A4_HEIGHT;

      const fitZoom = Math.min(horizontalZoom, verticalZoom);
      const safeZoom = Math.max(fitZoom, 0.1);

      // 現在のズーム値が最小ズーム値より小さい場合のみ更新
      if (zoom < safeZoom) {
        setZoom(safeZoom);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [zoomInitialized, zoom]); // zoomの変更も監視

  if (isLoading || !project || !currentDrawingImage) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white rounded-md max-h-[calc(100vh-90px)] relative">
      <Toolbar
        setZoom={updateZoom}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        drawingImages={drawingImages ?? []}
        zoom={zoom}
        showFlag={showFlag}
        setShowFlag={setShowFlag}
        showDownloader={showDownloader}
        setShowDownloader={setShowDownloader}
      />
      <div
        ref={containerRef}
        className="flex-1 bg-black/80 overflow-auto rounded-b-md border-x border-b border-gray-300"
        style={{
          height: "calc(100vh - 128px)",
          maxHeight: "calc(100vh - 128px)",
          overflow: "auto",
        }}
      >
        <div
          className="flex items-center justify-center min-h-full min-w-full"
          style={{
            width: `${A4_WIDTH * zoom}px`,
            height: `${A4_HEIGHT * zoom}px`,
          }}
        >
          <DrawingArea
            project={project}
            officeName={officeName}
            zoom={zoom}
            drawing={drawing}
            currentIndex={currentIndex}
            totalCount={drawingImages?.length ?? 0}
            currentDrawingImage={currentDrawingImage}
            quantityId={quantityId}
            showFlag={showFlag}
          />
        </div>
      </div>
      {showDownloader && (
        <Downloader
          project={project}
          officeName={officeName}
          zoom={zoom}
          drawing={drawing}
          totalCount={drawingImages?.length ?? 0}
          drawingImages={drawingImages ?? []}
          quantityId={quantityId}
          showFlag={showFlag}
          setShowDownloader={setShowDownloader}
        />
      )}
    </div>
  );
};
